import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/vendly';
import styles from './SellerDashboardPage.module.css';

const formatPrice = (n) => `₦${Number(n).toLocaleString()}`;

const STATUS_STYLE = {
  active:   { bg: '#E8F5E9', color: '#1B5E20', label: 'Live' },
  pending:  { bg: '#FFF3CD', color: '#856404', label: 'Under review' },
  declined: { bg: '#FFEBEE', color: '#B71C1C', label: 'Declined' },
};

export default function SellerDashboardPage() {
  const [seller,   setSeller]   = useState(null);
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/sellers/me'),
      api.get('/sellers/me/products').catch(() => ({ data: { products: [] } })),
    ])
      .then(([selRes, prodRes]) => {
        setSeller(selRes.data.seller);
        setProducts(prodRes.data.products || []);
      })
      .catch(() => setError('Failed to load seller data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className={styles.loading}>
      <div className={styles.loadingSpinner} />
      <p>Loading your seller account…</p>
    </div>
  );

  if (error) return (
    <div className={styles.errorState}>
      <p>{error}</p>
      <Link to="/become-seller" className={styles.applyBtn}>Apply to become a seller</Link>
    </div>
  );

  if (!seller) return (
    <div className={styles.noSeller}>
      <span>🏪</span>
      <h2>No seller account found</h2>
      <p>You haven't applied to become a seller yet.</p>
      <Link to="/become-seller" className={styles.applyBtn}>Become a seller</Link>
    </div>
  );

  // Pending state
  if (seller.status === 'pending') return (
    <div className={styles.pendingState}>
      <span>⏳</span>
      <h2>Application under review</h2>
      <p>Your seller application for <strong>{seller.businessName}</strong> is being reviewed. You'll be notified within 24 hours.</p>
      <div className={styles.sellerIdBox}>
        <p className={styles.sellerIdLabel}>Your Seller ID (save this)</p>
        <p className={styles.sellerId}>{seller.sellerId}</p>
      </div>
    </div>
  );

  // Declined state
  if (seller.status === 'declined') return (
    <div className={styles.declinedState}>
      <span>❌</span>
      <h2>Application declined</h2>
      <p className={styles.declineReason}>Reason: {seller.declineReason || 'No reason provided'}</p>
      <p>Please fix the issues and reapply.</p>
      <Link to="/seller/apply" className={styles.applyBtn}>Reapply</Link>
    </div>
  );

  // Approved — full dashboard
  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.shopAvatar}>{seller.businessName.charAt(0).toUpperCase()}</div>
          <div>
            <h2 className={styles.shopName}>{seller.businessName}</h2>
            <p className={styles.shopCategory}>{seller.category} · {seller.state}</p>
          </div>
        </div>
        <div className={styles.sellerIdBadge}>
          <span className={styles.sellerIdBadgeLabel}>Seller ID</span>
          <span className={styles.sellerIdBadgeVal}>{seller.sellerId}</span>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statNum}>{products.length}</span>
          <span className={styles.statLabel}>Total products</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNum}>{products.filter(p => p.isActive).length}</span>
          <span className={styles.statLabel}>Live products</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNum}>₦0</span>
          <span className={styles.statLabel}>Total earnings</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNum}>0</span>
          <span className={styles.statLabel}>Orders received</span>
        </div>
      </div>

      {/* Upload methods */}
      <div className={styles.uploadSection}>
        <h3 className={styles.uploadTitle}>Upload a product</h3>
        <div className={styles.uploadOptions}>
          <a
            href="https://t.me/Ven_Upload_bot"
            target="_blank"
            rel="noreferrer"
            className={styles.uploadCard}
          >
            <div className={styles.uploadIcon} style={{ background: '#229ED9' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-2.04 9.613c-.153.681-.555.849-1.124.528l-3.1-2.285-1.496 1.44c-.165.165-.304.304-.624.304l.222-3.156 5.74-5.185c.249-.222-.054-.345-.386-.123L7.06 14.316l-3.07-.957c-.666-.208-.68-.666.14-.986l11.99-4.623c.555-.2 1.041.136.442.498z"/>
              </svg>
            </div>
            <div className={styles.uploadInfo}>
              <p className={styles.uploadName}>Upload via Telegram</p>
              <p className={styles.uploadDesc}>Use your Seller ID <strong>{seller.sellerId}</strong> to start</p>
            </div>
            <span className={styles.uploadArrow}>→</span>
          </a>

          <div className={`${styles.uploadCard} ${styles.uploadCardDisabled}`}>
            <div className={styles.uploadIcon} style={{ background: '#9CA3AF' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <div className={styles.uploadInfo}>
              <p className={styles.uploadName}>Upload via website</p>
              <p className={styles.uploadDesc}>Coming soon — in development</p>
            </div>
            <span className={styles.devBadge}>Soon</span>
          </div>
        </div>
      </div>

      {/* Products list */}
      <div className={styles.productsSection}>
        <div className={styles.productsHeader}>
          <h3 className={styles.productsTitle}>My products ({products.length})</h3>
        </div>

        {products.length === 0 ? (
          <div className={styles.emptyProducts}>
            <span>📦</span>
            <p>You haven't uploaded any products yet.</p>
            <p className={styles.emptyHint}>Use your Seller ID <strong>{seller.sellerId}</strong> on the Telegram bot to upload your first product.</p>
          </div>
        ) : (
          <div className={styles.productsList}>
            {products.map(p => {
              const st = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
              return (
                <div key={p.id} className={styles.productRow}>
                  <img src={p.image} alt={p.name} className={styles.productImg} />
                  <div className={styles.productInfo}>
                    <p className={styles.productName}>{p.name}</p>
                    <p className={styles.productCat}>{p.category}</p>
                  </div>
                  <p className={styles.productPrice}>{formatPrice(p.price)}</p>
                  <span className={styles.productStatus} style={{ background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
